// src/components/cofounder/ConversationSidebar.js
import React, { useState } from 'react';
import { Plus, MessageSquare, Trash2, Edit3, Check, X, Search } from 'lucide-react';
import { useCoFounder } from '../../context/CoFounderContext';

const ConversationSidebar = ({ className = '' }) => {
  const {
    conversations, activeConversationId,
    startNewConversation, selectConversation,
    deleteConversation, renameConversation
  } = useCoFounder();

  const [searchQuery, setSearchQuery] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');

  const filtered = conversations.filter(c =>
    !searchQuery || c.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleStartEdit = (e, convo) => {
    e.stopPropagation();
    setEditingId(convo.id);
    setEditTitle(convo.title || '');
  };

  const handleSaveEdit = (e) => {
    e.stopPropagation();
    if (editTitle.trim()) {
      renameConversation(editingId, editTitle.trim());
    }
    setEditingId(null);
  };

  const handleCancelEdit = (e) => {
    e.stopPropagation();
    setEditingId(null);
  };

  const handleDelete = (e, id) => {
    e.stopPropagation();
    if (window.confirm('Delete this conversation?')) {
      deleteConversation(id);
    }
  };

  return (
    <div className={`flex flex-col bg-gray-900 text-white ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-700">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-sm">Conversations</h3>
          <button
            onClick={startNewConversation}
            className="p-1.5 rounded-lg bg-[#190192] hover:bg-[#0f0075] transition-colors"
            title="New conversation"
          >
            <Plus size={14} />
          </button>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search..."
            className="w-full bg-gray-800 text-sm text-white rounded-lg pl-8 pr-3 py-1.5
              placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#190192]"
          />
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="p-4 text-center text-gray-500 text-xs">
            {searchQuery ? 'No matches' : 'No conversations yet'}
          </div>
        ) : (
          <div className="py-1">
            {filtered.map(convo => (
              <div
                key={convo.id}
                onClick={() => selectConversation(convo.id)}
                className={`group flex items-center gap-2 px-4 py-2.5 cursor-pointer transition-colors
                  ${convo.id === activeConversationId
                    ? 'bg-[#190192] bg-opacity-30'
                    : 'hover:bg-gray-800'}`}
              >
                <MessageSquare size={14} className="flex-shrink-0 text-gray-400" />

                {editingId === convo.id ? (
                  <div className="flex-1 flex items-center gap-1">
                    <input
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      className="flex-1 bg-gray-700 text-sm text-white rounded px-2 py-0.5 focus:outline-none"
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSaveEdit(e); if (e.key === 'Escape') handleCancelEdit(e); }}
                    />
                    <button onClick={handleSaveEdit} className="text-green-400 hover:text-green-300"><Check size={12} /></button>
                    <button onClick={handleCancelEdit} className="text-gray-400 hover:text-gray-300"><X size={12} /></button>
                  </div>
                ) : (
                  <>
                    <span className="flex-1 text-sm truncate text-gray-300">
                      {convo.title || 'New conversation'}
                    </span>
                    <div className="hidden group-hover:flex items-center gap-0.5">
                      <button onClick={(e) => handleStartEdit(e, convo)} className="p-1 text-gray-500 hover:text-gray-300">
                        <Edit3 size={11} />
                      </button>
                      <button onClick={(e) => handleDelete(e, convo.id)} className="p-1 text-gray-500 hover:text-red-400">
                        <Trash2 size={11} />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationSidebar;
